/// API client for communicating with Cloudflare Pages Functions

const API_BASE = "/api";

const ARTWORK_OVERRIDES: Record<string, Partial<Artwork>> = {
  "birds-in-bloom": {
    title_it: "Whispers of Spring",
    title_en: "Whispers of Spring",
    technique: "Oil on canvas",
    status: "available",
  },
  "garden-light": {
    title_it: "The Secret Garden",
    title_en: "The Secret Garden",
    technique: "Oil on canvas",
    status: "available",
  },
};

function normalizeArtwork<T extends Artwork>(artwork: T): T {
  const override = ARTWORK_OVERRIDES[artwork.slug];
  if (!override) return artwork;

  return { ...artwork, ...override };
}

async function fetchApi<T>(path: string, options?: RequestInit): Promise<T> {
  const response = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...options?.headers,
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: "Request failed" }));
    throw new Error((error as { error: string }).error || `HTTP ${response.status}`);
  }

  return response.json() as Promise<T>;
}

// ─── Artworks ─────────────────────────────────────────────────────────────────

export interface Artwork {
  id: number;
  slug: string;
  title_it: string;
  description_it: string | null;
  title_en: string;
  description_en: string | null;
  category: "print" | "original";
  technique: string | null;
  dimensions: string | null;
  year: number | null;
  price: number;
  currency: string;
  status: "available" | "sold" | "reserved";
  featured: number;
  image_key: string | null;
  image_url: string | null;
  additional_images: string | null;
  stock_quantity?: number;
  created_at: string;
  updated_at: string;
}

export interface ArtworksResponse {
  artworks: Artwork[];
  total: number;
}

export async function getArtworks(params?: {
  category?: string;
  status?: string;
  featured?: boolean;
  search?: string;
  limit?: number;
  offset?: number;
}): Promise<ArtworksResponse> {
  const searchParams = new URLSearchParams();
  if (params?.category) searchParams.set("category", params.category);
  if (params?.status) searchParams.set("status", params.status);
  if (params?.featured) searchParams.set("featured", "true");
  if (params?.search) searchParams.set("search", params.search);
  if (params?.limit) searchParams.set("limit", params.limit.toString());
  if (params?.offset) searchParams.set("offset", params.offset.toString());

  const query = searchParams.toString();
  const response = await fetchApi<ArtworksResponse>(`/artworks${query ? `?${query}` : ""}`);
  return {
    ...response,
    artworks: response.artworks.map(normalizeArtwork),
  };
}

export async function getArtworkBySlug(slug: string): Promise<Artwork> {
  const artwork = await fetchApi<Artwork>(`/artworks?slug=${slug}`);
  return normalizeArtwork(artwork);
}

// ─── Auth ─────────────────────────────────────────────────────────────────────

export interface User {
  id: number;
  email: string;
  name: string | null;
  role: string;
}

export async function login(email: string, password: string): Promise<{ user: User }> {
  return fetchApi<{ user: User }>("/auth/login", {
    method: "POST",
    body: JSON.stringify({ email, password }),
  });
}

export async function register(email: string, password: string, name?: string): Promise<{ user: User }> {
  return fetchApi<{ user: User }>("/auth/register", {
    method: "POST",
    body: JSON.stringify({ email, password, name }),
  });
}

export async function logout(): Promise<void> {
  await fetchApi("/auth/logout", { method: "POST" });
}

export async function getMe(): Promise<{ user: User | null }> {
  return fetchApi<{ user: User | null }>("/auth/me");
}

// ─── Orders ───────────────────────────────────────────────────────────────────

export interface CheckoutData {
  items: Array<{ artworkId: number; quantity: number }>;
  customerName: string;
  customerEmail: string;
  customerPhone?: string;
  shippingAddress: string;
  notes?: string;
  locale?: string;
}

export async function createCheckout(data: CheckoutData): Promise<{ url?: string; orderId?: number }> {
  return fetchApi<{ url?: string; orderId?: number }>("/orders/checkout", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export interface Order {
  id: number;
  user_id: number | null;
  artwork_id: number;
  stripe_payment_intent_id: string | null;
  stripe_checkout_session_id: string | null;
  status: "pending" | "paid" | "processing" | "shipped" | "delivered" | "cancelled" | "refunded";
  total_amount: number;
  shipping_amount: number | null;
  currency: string;
  customer_name: string | null;
  customer_email: string | null;
  customer_phone: string | null;
  shipping_address: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
  item_titles: string | null;
}

export async function getOrders(): Promise<{ orders: Order[] }> {
  return fetchApi<{ orders: Order[] }>("/orders");
}

export async function updateOrderStatus(id: number, status: Order["status"]): Promise<{ success: boolean }> {
  return fetchApi<{ success: boolean }>(`/orders/${id}/status`, {
    method: "PATCH",
    body: JSON.stringify({ status }),
  });
}

// ─── Contact ──────────────────────────────────────────────────────────────────

export async function sendContactMessage(data: {
  name: string;
  email: string;
  subject?: string;
  message: string;
}): Promise<{ success: boolean }> {
  return fetchApi<{ success: boolean }>("/contact", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export interface ContactMessage {
  id: number;
  name: string;
  email: string;
  subject: string | null;
  message: string;
  read: number;
  created_at: string;
}

// ─── Admin ────────────────────────────────────────────────────────────────────

export interface AdminStats {
  totalArtworks: number;
  availableArtworks: number;
  soldArtworks: number;
  totalOrders: number;
  totalRevenue: number;
  unreadMessages: number;
  pendingOrders?: number;
  reservedArtworks?: number;
  recentOrders?: Order[];
}

export async function getAdminStats(): Promise<AdminStats> {
  return fetchApi<AdminStats>("/admin/stats");
}

export interface StoreSettings {
  announcement_bar: string;
  contact_email: string;
  contact_phone: string;
  studio_address: string;
  shipping_note: string;
  checkout_note: string;
}

export async function getStoreSettings(): Promise<StoreSettings> {
  return fetchApi<StoreSettings>("/admin/settings");
}

export async function saveStoreSettings(settings: StoreSettings): Promise<StoreSettings> {
  return fetchApi<StoreSettings>("/admin/settings", {
    method: "PUT",
    body: JSON.stringify(settings),
  });
}

export async function getContactMessages(): Promise<{ messages: ContactMessage[] }> {
  return fetchApi<{ messages: ContactMessage[] }>("/contact");
}

export async function markContactMessageRead(id: number): Promise<{ success: boolean }> {
  return fetchApi<{ success: boolean }>(`/contact/${id}/read`, { method: "PATCH" });
}

export async function createArtwork(data: Partial<Artwork>): Promise<{ id: number; slug: string }> {
  return fetchApi<{ id: number; slug: string }>("/artworks", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function updateArtwork(id: number, data: Partial<Artwork>): Promise<{ success: boolean }> {
  return fetchApi<{ success: boolean }>(`/artworks/${id}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });
}

export async function deleteArtwork(id: number): Promise<{ success: boolean }> {
  return fetchApi<{ success: boolean }>(`/artworks/${id}`, {
    method: "DELETE",
  });
}

export async function uploadImage(file: File, folder?: string): Promise<{ key: string; url: string }> {
  const formData = new FormData();
  formData.append("file", file);
  if (folder) formData.append("folder", folder);

  const response = await fetch(`${API_BASE}/storage/upload`, {
    method: "POST",
    body: formData,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: "Upload failed" }));
    throw new Error((error as { error: string }).error);
  }

  return response.json() as Promise<{ key: string; url: string }>;
}
